// Ollaway conversion layer: offer selector, rating chips, review clip, global buy bar, cart nudge.
// Everything here enhances markup that already works without JavaScript.
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const config = (() => {
    try { return JSON.parse(document.getElementById('OllawayOfferConfig').textContent); }
    catch { return {}; }
  })();
  const money = cents => new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100);

  const freePairKey = 'ollaway_free_pair_v1';
  let claimedCode = '';
  try { claimedCode = localStorage.getItem(freePairKey) || ''; } catch {}
  if (!claimedCode) { try { claimedCode = sessionStorage.getItem(freePairKey) || ''; } catch {} }
  const freePair = window.ollawayFreePair = {
    unlocked: () => Boolean(config.freePairCode && claimedCode === config.freePairCode),
    unlock() {
      claimedCode = config.freePairCode;
      try { localStorage.setItem(freePairKey, claimedCode); } catch {}
      try { sessionStorage.setItem(freePairKey, claimedCode); } catch {}
      document.dispatchEvent(new CustomEvent('ollaway:free-pair'));
    },
    async apply() {
      if (!this.unlocked()) throw new Error('Enter your email to unlock your free pair first.');
      const response = await fetch(`/discount/${encodeURIComponent(config.freePairCode)}?redirect=/cart`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('We couldn’t apply your free-pair offer. Please try again.');
    }
  };

  /* Rating chips: numbers come from the testimonials section, never hardcoded. */
  function fillRatings() {
    const source = document.querySelector('[data-ol-rating-data]');
    if (!source) return;
    let data;
    try { data = JSON.parse(source.textContent); } catch { return; }
    if (!data.count) return;
    const average = Number(data.average).toFixed(1);
    document.querySelectorAll('[data-ol-rating-chip]').forEach(chip => {
      chip.querySelector('[data-ol-rating-average]').textContent = average;
      chip.querySelector('[data-ol-rating-count]').textContent = `${data.count} reviews`;
      chip.querySelector('[data-ol-rating-fill]').style.setProperty('--ol-fill', `${data.average * 20}%`);
      if (chip.tagName === 'A') chip.setAttribute('aria-label', `Rated ${average} out of 5 from ${data.count} reviews. Read the reviews.`);
      chip.hidden = false;
    });
  }

  /* Distribution bars grow in once, when seen. */
  function watchSummary() {
    const summary = document.querySelector('[data-ol-rating-summary]');
    if (!summary) return;
    if (reduced.matches || !('IntersectionObserver' in window)) return summary.classList.add('is-in');
    const observer = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) return;
      summary.classList.add('is-in');
      observer.disconnect();
    }, { threshold: 0.4 });
    observer.observe(summary);
  }

  /* Review wall: clipped with a fade, expands in place. */
  function clipReviews() {
    const clip = document.querySelector('[data-ol-review-clip]');
    const more = document.querySelector('[data-ol-review-more]');
    if (!clip || !more || (window.Shopify && Shopify.designMode)) return;
    const button = more.querySelector('button');
    const limit = () => (innerWidth < 750 ? 2100 : 1500);
    if (clip.scrollHeight < limit() + 500) return;
    clip.style.setProperty('--ol-clip', `${limit()}px`);
    clip.classList.add('is-collapsed');
    more.hidden = false;
    // Anything the fade covers should not take keyboard focus.
    button.addEventListener('click', () => {
      const done = () => {
        clip.classList.remove('is-collapsed', 'is-expanding');
        clip.style.removeProperty('max-height');
        more.hidden = true;
        clip.querySelector('.ollaway-review-card a, .ollaway-review-card')?.focus?.({ preventScroll: true });
      };
      button.setAttribute('aria-expanded', 'true');
      // Cards below the clip never intersected the viewport, so the reveal observer never fired for
      // them. Show them now, or "Show all" opens onto holes until you scroll past each one.
      clip.querySelectorAll('.ol-r:not(.ol-in), .ol-h:not(.ol-in)').forEach((el) => el.classList.add('ol-in'));
      if (reduced.matches) return done();
      clip.style.maxHeight = `${clip.offsetHeight}px`;
      clip.classList.add('is-expanding');
      clip.classList.remove('is-collapsed');
      requestAnimationFrame(() => { clip.style.maxHeight = `${clip.scrollHeight}px`; });
      clip.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 1200);
    }, { once: true });
  }

  /* Offer selector */
  function initOffer(box) {
    const form = box.querySelector('form[data-ol-offer-form]');
    if (!form) return;
    const button = form.querySelector('[data-ol-add]');
    const label = form.querySelector('[data-ol-add-label]');
    const total = form.querySelector('[data-ol-add-total]');
    const error = form.querySelector('[data-ol-add-error]');
    const selected = () => form.querySelector('[data-ol-tier]:checked');
    const sync = () => {
      const tier = selected();
      if (total) total.textContent = tier && !button.disabled ? tier.dataset.totalText : '';
      document.dispatchEvent(new CustomEvent('ollaway:tier', { detail: { box } }));
    };
    form.addEventListener('change', event => {
      if (!event.target.matches('[data-ol-tier]')) return;
      const card = event.target.closest('.ol-tier');
      card.classList.remove('is-picked');
      void card.offsetWidth;
      card.classList.add('is-picked');
      sync();
    });
    sync();
    document.addEventListener('ollaway:price', sync);
    form.addEventListener('submit', async event => {
      if (!window.fetch || !config.cartAddUrl) return;
      event.preventDefault();
      if (button.getAttribute('aria-busy') === 'true') return;
      const idle = label.textContent;
      error.hidden = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = 'Adding…';
      try {
        const tier = selected();
        if (config.bundlesLive && tier?.name === 'quantity' && Number(tier.value) >= 3) await freePair.apply();
        const response = await fetch(config.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'That didn’t go through. Please try again.');
        button.classList.add('is-added');
        label.textContent = 'Added';
        setTimeout(() => { location.href = config.cartUrl || '/cart'; }, reduced.matches ? 0 : 450);
      } catch (problem) {
        error.textContent = problem.message;
        error.hidden = false;
        label.textContent = idle;
        button.removeAttribute('aria-busy');
      }
    });
    // Coming back with the back button should not leave a spinner.
    addEventListener('pageshow', event => {
      if (!event.persisted) return;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-added');
      label.textContent = 'Add to cart';
    });
  }

  /* Global buy bar */
  function initSticky() {
    const bar = document.querySelector('[data-ol-sticky]');
    const box = document.querySelector('[data-ol-buybox]');
    if (!bar || !box) return;
    const cta = bar.querySelector('[data-ol-sticky-cta]');
    const tierText = bar.querySelector('[data-ol-sticky-tier]');
    const priceText = bar.querySelector('[data-ol-sticky-price]');
    const compareText = bar.querySelector('[data-ol-sticky-compare]');
    const form = box.querySelector('form[data-ol-offer-form]');
    const add = form?.querySelector('[data-ol-add]');
    const buyable = Boolean(add && !add.disabled);
    const mirror = () => {
      const tier = form?.querySelector('[data-ol-tier]:checked');
      tierText.textContent = buyable && tier ? tier.dataset.label : '';
      priceText.textContent = buyable && tier ? tier.dataset.totalText : '';
      const compare = Number(tier?.dataset.compare);
      compareText.hidden = !(buyable && tier && compare > Number(tier.dataset.total));
      compareText.textContent = compareText.hidden ? '' : money(compare);
    };
    mirror();
    document.addEventListener('ollaway:tier', mirror);
    cta.addEventListener('click', () => {
      if (buyable && form.querySelector('[data-ol-tier]:checked')) return form.requestSubmit(add);
      const target = document.getElementById('ollaway-offer') || box;
      target.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
    });
    if (add) new MutationObserver(() => {
      const busy = add.getAttribute('aria-busy') === 'true';
      cta.toggleAttribute('disabled', busy);
      cta.textContent = add.classList.contains('is-added') ? 'Added' : busy ? 'Adding…' : 'Add to cart';
    }).observe(add, { attributes: true });

    if (!('IntersectionObserver' in window)) return;
    const seen = new Map();
    let passedBox = false;
    const update = () => {
      const blocked = [...seen.values()].some(Boolean);
      const show = passedBox && !blocked;
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', String(!show));
      if (show) document.body.style.setProperty('--ollaway-sticky-height', `${bar.offsetHeight + 12}px`);
      document.body.classList.toggle('ol-sticky-on', show);
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        seen.set(entry.target, entry.isIntersecting);
        if (entry.target === box) passedBox = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      }
      update();
    });
    observer.observe(box);
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    if (footer) observer.observe(footer);
  }

  /* Cart nudge: sits under the line items and always points at the buy-2-get-1 bundle.
     1 pair  -> "Buy 2, get 1 free": add two more, pay for two.
     2 pairs -> add a third pair to get the bundle.
     3+      -> nothing to add. The nudge appears only after email signup unlocks the offer. */
  const esc = value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  let lastNudgeQty = null;
  async function initCartNudge() {
    if (!config.bundlesLive || !config.singleVariant || !config.productId) return;
    if (!freePair.unlocked()) { document.querySelectorAll('.ol-nudge').forEach(n => n.remove()); return; }
    const footer = document.getElementById('main-cart-footer');
    if (!footer || !window.fetch) return;
    let cart;
    try { cart = await (await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json(); } catch { return; }
    const lines = cart.items.filter(item => item.product_id === config.productId);
    const qty = lines.reduce((sum, item) => sum + item.quantity, 0);
    document.documentElement.dataset.olBundleQty = String(qty);
    document.dispatchEvent(new CustomEvent('ollaway:cart', { detail: { bundleQty: qty, cart } }));
    if (lines.length !== 1 || cart.items.length !== 1 || qty >= 3) { document.querySelectorAll('.ol-nudge').forEach(n => n.remove()); lastNudgeQty = qty; return; }
    // Only rebuild when the quantity actually changed — the cart repaints often and the card must not jump.
    if (qty === lastNudgeQty && document.querySelector('.ol-nudge')) return;
    lastNudgeQty = qty;
    const line = lines[0];
    if (!(config.tiers[2] > 0)) return;
    document.querySelectorAll('.ol-nudge').forEach(n => n.remove());
    const card = document.createElement('div');
    card.className = 'ol-nudge';
    const head = qty === 1 ? 'Buy 2, get 1 free.' : 'Your third pair is free.';
    const body = qty === 1
      ? 'Add two more pairs and the third one is on us.'
      : 'Buy 2, get 1 free — one for each seat, plus a spare.';
    const label = qty === 1 ? 'Make it 3 pairs' : 'Add the free pair';
    card.innerHTML = `<div class="ol-nudge__card"><p><strong>${esc(head)}</strong><span>${esc(body)}</span></p><button type="button" class="button">${esc(label)}</button></div>`;
    const itemsSection = footer.closest('.shopify-section')?.previousElementSibling;
    (itemsSection || footer.closest('.shopify-section')).insertAdjacentElement(itemsSection ? 'afterend' : 'beforebegin', card);
    const button = card.querySelector('button');
    button.addEventListener('click', async () => {
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        await freePair.apply();
        const response = await fetch(config.cartChangeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 3 }) });
        if (!response.ok) throw new Error();
        location.reload();
      } catch {
        button.disabled = false;
        button.textContent = 'Try again';
      }
    });
  }

  document.addEventListener('ollaway:free-pair', () => initCartNudge());

  // Dawn changes quantities without a page reload and re-renders the cart sections; run the cart pieces again when that happens.
  let cartRefresh;
  function watchCartUpdates() {
    const rerun = () => { clearTimeout(cartRefresh); cartRefresh = setTimeout(() => { initCartNudge(); document.dispatchEvent(new CustomEvent('ollaway:refresh')); }, 250); };
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) subscribe(PUB_SUB_EVENTS.cartUpdate, rerun);
    const footer = document.getElementById('main-cart-footer');
    if (footer && 'MutationObserver' in window) new MutationObserver(rerun).observe(footer, { childList: true, subtree: true });
    const items = document.getElementById('main-cart-items');
    if (items && 'MutationObserver' in window) new MutationObserver(rerun).observe(items, { childList: true });
  }

  function start() {
    fillRatings();
    watchCartUpdates();
    watchSummary();
    clipReviews();
    document.querySelectorAll('[data-ol-buybox]').forEach(initOffer);
    initSticky();
    initCartNudge();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', () => { fillRatings(); watchSummary(); });
})();
